import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Tooltip, Typography } from '@material-ui/core';
import { AttachFile } from '@material-ui/icons';
import { Filezone, Grid, Label } from '../../common/components';
import { PDF_TYPE } from '../../common/constants';
import { media } from '../../common/styles';
import messages from '../messages';

const S = {
  Filezone: styled(({ hidden, ...otherProps }) => <Filezone {...otherProps} />)`
    min-width: 90px;
    margin-right: 8px;
    ${({ hidden }) => hidden && css`
      display: none;
    `}
  `,
  HelperText: styled.p`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 0.75rem;
    line-height: 1.5;
    ::before {
      content: '*';
      margin-right: 2px;
    }
  `,
  Boundary: styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    padding: 0 16px 0 8px;
    border: 1px solid ${({ theme }) => theme.palette.grey[400]};
    border-radius: 4px;
    :hover {
      border-color: rgba(0, 0, 0, 0.87);
    }
  `,
  AttachFile: styled(AttachFile)`
    margin-right: 4px;
    color: ${({ theme }) => theme.palette.grey[500]};
  `,
  FileName: styled(Typography)`
    overflow: hidden;
    width: 420px;
    margin-right: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    white-space: nowrap;
    text-overflow: ellipsis;
    ${media.lg`
      width: 360px;
    `}
    ${media.xl`
      width: 480px;
    `}
  `,
  Tooltip: styled(({ classes, className, ...otherProps }) => (
    <Tooltip
      className={className}
      classes={{
        ...classes,
        popper: className,
        tooltip: 'tooltip',
      }}
      {...otherProps}
    />
  ))`
    .tooltip {
      position: relative;
      padding: 6px 16px;
      background-color: ${({ theme }) => theme.palette.grey[700]};
      font-weight: 400;
    }
    .tooltip::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translate(-50%, 100%);
      border: 5px solid transparent;
      border-top: 7px solid ${({ theme }) => theme.palette.grey[700]};
    }
  `,
  Button: styled.button`
    color: ${({ theme }) => theme.palette.primary.light};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    cursor: pointer;
    outline: none;
    :hover {
      text-decoration: underline;
    }
  `,
  Input: styled.input`
    display: none;
  `,
};

const FileField = ({
  className, field: { value, onChange, ...field }, form, intl, onChangePdf, ...otherProps
}) => {
  const error = useMemo(() => !!(
    form.errors[field.name] && form.touched[field.name]
  ), [field.name, form.errors, form.touched]);
  const handleChange = useCallback((event) => {
    const [file] = event.target.files;

    form.setFieldTouched(field.name, true);
    form.setFieldValue('questions', null);
    form.setFieldValue('signatures', null);
    form.setFieldValue(field.name, file || null);

    onChangePdf();
  }, [field.name, form, onChangePdf]);
  const label = useRef(null);

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      className={className}
    >
      <Grid item xs={1}>
        <Label
          ref={label}
          htmlFor={field.name}
          error={error}
          required
        >
          <FormattedMessage {...messages.fileFieldLabel} />
        </Label>
      </Grid>
      <Grid
        container
        item
        xs={6}
        alignItems="flex-end"
      >
        <S.Filezone
          id={field.name}
          accept={PDF_TYPE}
          error={error}
          onChange={handleChange}
          hidden={value}
          {...field}
          {...otherProps}
        >
          <FormattedMessage {...messages.fileFieldFilezone} />
        </S.Filezone>
        {value
          ? (
            <S.Boundary>
              <S.AttachFile />
              <S.FileName>
                {value.name || '제목 없음'}
              </S.FileName>
              <S.Tooltip title={intl.formatMessage(messages.fileFieldTooltip)} placement="top">
                <S.Button type="button" onClick={() => label.current.click()}>
                  <FormattedMessage {...messages.fileFieldButtonFileChange} />
                </S.Button>
              </S.Tooltip>
            </S.Boundary>
          )
          : (
            <S.HelperText>
              <FormattedMessage {...messages.fileFieldHelperText} />
            </S.HelperText>
          )}
      </Grid>
    </Grid>
  );
};

FileField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({}),
    ]),
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({
      file: PropTypes.string,
    }).isRequired,
    touched: PropTypes.shape({
      file: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
    }).isRequired,
    setFieldTouched: PropTypes.func,
    setFieldValue: PropTypes.func,
  }).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  onChangePdf: PropTypes.func.isRequired,
};

FileField.defaultProps = {
  className: undefined,
};

export default injectIntl(FileField);
